import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MagicLinkService } from '../services/magicLinkService';
import { VoteGroupService } from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';
import { Member, VoteGroup } from '../types';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  UserIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const IndividualVotePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagicLink, isMember } = useAuth();
  
  const [member, setMember] = useState<Member | null>(null);
  const [activeVoteGroup, setActiveVoteGroup] = useState<VoteGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleMagicLink = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Token manquant dans l\'URL');
        setLoading(false);
        return;
      }

      try {
        // Valider le lien magique
        const validatedMember = await MagicLinkService.validateAndUseMagicLink(token);
        
        if (!validatedMember) {
          setError('Lien magique invalide ou expiré');
          setLoading(false);
          return;
        }

        setMember(validatedMember);

        // Connecter l'utilisateur en tant que membre
        await loginWithMagicLink(validatedMember);

        // Vérifier s'il y a un groupe de vote actif
        const activeGroup = await VoteGroupService.getActiveVoteGroup();
        
        if (activeGroup) {
          setActiveVoteGroup(activeGroup);
          
          // Vérifier si le membre a déjà voté dans ce groupe
          const hasVoted = await MagicLinkService.hasMemberVotedInGroup(validatedMember.id, activeGroup.id);
          
          if (hasVoted) {
            // Rediriger vers la page des choix déjà faits
            navigate(`/vote/choices/${activeGroup.id}?token=${token}`);
            return;
          }
        }

      } catch (error: any) {
        console.error('Erreur lors de la validation du lien magique:', error);
        setError(error.message || 'Erreur lors de la validation du lien magique');
      } finally {
        setLoading(false);
      }
    };

    handleMagicLink();
  }, [searchParams, navigate, loginWithMagicLink]);

  const handleStartVoting = () => {
    if (activeVoteGroup) {
      navigate(`/vote/group/${activeVoteGroup.id}?token=${searchParams.get('token')}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Connexion en cours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  if (!member || !isMember) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Connexion échouée</h1>
          <p className="text-gray-600">Impossible de vous connecter en tant que membre.</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 btn-primary"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* En-tête */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
            Bienvenue, {member.firstName} {member.lastName} !
          </h1>
          
          <p className="text-gray-600 text-center mb-6">
            Vous êtes connecté(e) via un lien magique sécurisé
          </p>

          {/* Informations du membre */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-center mb-4">
              <UserIcon className="w-6 h-6 text-gray-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Vos informations</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center">
                <span className="font-medium text-gray-700 w-24">Email :</span>
                <span className="text-gray-600">{member.email}</span>
              </div>
              
              {member.department && (
                <div className="flex items-center">
                  <span className="font-medium text-gray-700 w-24">Département :</span>
                  <span className="text-gray-600">{member.department}</span>
                </div>
              )}
              
              <div className="flex items-center">
                <span className="font-medium text-gray-700 w-24">Statut :</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  member.membershipStatus === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : member.membershipStatus === 'paused'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {member.membershipStatus === 'active' ? 'Actif' : 
                   member.membershipStatus === 'paused' ? 'En pause' : 'Inactif'}
                </span>
              </div>
              
              <div className="flex items-center">
                <span className="font-medium text-gray-700 w-24">Droit de vote :</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  member.hasVotingRights 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {member.hasVotingRights ? 'Oui' : 'Non'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Groupe de vote actif */}
        {activeVoteGroup ? (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-blue-100 p-3 rounded-full">
                <ShieldCheckIcon className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Groupe de vote actif
            </h2>
            
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-2">
                {activeVoteGroup.name}
              </h3>
              
              {activeVoteGroup.description && (
                <p className="text-blue-700 mb-4">{activeVoteGroup.description}</p>
              )}
              
              <div className="flex items-center text-blue-600 mb-4">
                <ClockIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">
                  Du {activeVoteGroup.startDate.toLocaleDateString('fr-FR')} au {activeVoteGroup.endDate.toLocaleDateString('fr-FR')}
                </span>
              </div>
              
              <p className="text-sm text-blue-600">
                Ce groupe contient {activeVoteGroup.elections.length} élection(s)
              </p>
            </div>

            {member.hasVotingRights ? (
              <div className="text-center">
                <button
                  onClick={handleStartVoting}
                  className="btn-primary flex items-center mx-auto"
                >
                  <ArrowRightIcon className="w-5 h-5 mr-2" />
                  Commencer à voter
                </button>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <p className="text-yellow-800">
                  Vous n'avez pas le droit de vote pour le moment.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-gray-100 p-3 rounded-full">
                <ClockIcon className="w-8 h-8 text-gray-500" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Aucun vote en cours
            </h2>
            
            <p className="text-gray-600 mb-6">
              Il n'y a actuellement aucun groupe de vote actif.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                Vous serez automatiquement redirigé(e) vers le vote dès qu'un nouveau groupe sera activé.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndividualVotePage;
